import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import ESignatureDialog from "@/components/ESignatureDialog";
import AuditTrailPanel from "@/components/AuditTrailPanel";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, ArrowRight, ShieldCheck, AlertOctagon, Globe2, Link2 } from "lucide-react";
import { toast } from "sonner";

const STATES = ["Received", "Under Investigation", "Determination", "Closed"];
const HARM = ["None", "Malfunction", "Minor", "Serious", "Death"];
const REPORTER_TYPES = ["Customer", "HCP", "Distributor", "Internal", "Regulator"];

const emptyForm = {
  reporter_name: "", reporter_type: "Customer", contact_email: "",
  device_project_id: "", device_name: "", event_date: "", country: "",
  narrative: "", patient_harm: "None", device_involved_in_death_or_serious_injury: false,
};

const emptyDet = {
  root_cause_summary: "",
  fda_mdr_reportable: false, eu_mir_reportable: false, cdsco_reportable: false,
  rationale: "",
};

export default function Complaints() {
  const [list, setList] = useState([]);
  const [overview, setOverview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [openDet, setOpenDet] = useState(false);
  const [det, setDet] = useState(emptyDet);
  const [openCapa, setOpenCapa] = useState(false);
  const [capaOptions, setCapaOptions] = useState([]);
  const [selectedCapa, setSelectedCapa] = useState("");
  const [esignOpen, setEsignOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = async () => {
    setList((await api.get("/complaints")).data);
    setOverview((await api.get("/complaints/stats/overview")).data);
  };
  useEffect(() => { load(); }, []);
  const loadDetail = async (id) => setDetail((await api.get(`/complaints/${id}`)).data);

  const create = async () => {
    if (!form.narrative.trim()) return toast.error("Narrative required");
    try {
      const { data } = await api.post("/complaints", form);
      toast.success("Complaint logged", {
        description: `${data.complaint_id} — reportability hints computed`,
      });
      setOpenForm(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Log failed");
    }
  };

  const submitDetermination = async () => {
    if (!det.rationale.trim()) return toast.error("Rationale required");
    try {
      await api.post(`/complaints/${detail.complaint_id}/determination`, det);
      toast.success("Determination saved");
      setOpenDet(false);
      setDet(emptyDet);
      await loadDetail(detail.complaint_id);
      load();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const openLinkCapa = async () => {
    const { data } = await api.get("/capa");
    setCapaOptions(data.slice(0, 100));
    setSelectedCapa("");
    setOpenCapa(true);
  };

  const doLinkCapa = async () => {
    if (!selectedCapa) return toast.error("Pick a CAPA");
    try {
      await api.post(`/complaints/${detail.complaint_id}/link-capa`, { capa_id: selectedCapa });
      toast.success("CAPA linked");
      setOpenCapa(false);
      await loadDetail(detail.complaint_id);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Link failed");
    }
  };

  const requestTransition = (toState) => {
    setPending({ id: detail.complaint_id, toState });
    setEsignOpen(true);
  };

  const onSigned = async (sig) => {
    try {
      await api.post(`/complaints/${pending.id}/transition`, {
        to_state: pending.toState, signature_id: sig.signature_id, notes: sig.reason,
      });
      toast.success(`Advanced to ${pending.toState}`);
      setPending(null);
      await loadDetail(pending.id);
      load();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transition failed");
    }
  };

  const seriousHint = form.patient_harm === "Serious" || form.patient_harm === "Death" || form.device_involved_in_death_or_serious_injury;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="vq-overline mb-2">Quality Events</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">Complaints &amp; Post-Market Surveillance</h1>
            <p className="text-slate-600 mt-2 max-w-3xl">
              Field events, malfunctions, patient harm reports. Reportability suggestions align with 21 CFR 803 (FDA MDR),
              EU MDR Art. 87 (MIR), and CDSCO MvPI — final regulatory determination is signed off by QA/RA.
            </p>
          </div>
          <Button onClick={() => setOpenForm(true)} data-testid="complaint-new-btn"
                  className="h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Log Complaint
          </Button>
        </div>

        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="complaint-overview">
            <MetricCard label="Total" value={overview.total} />
            <MetricCard label="Received" value={overview.by_state?.Received || 0} />
            <MetricCard label="Investigating" value={overview.by_state?.["Under Investigation"] || 0} />
            <MetricCard label="Determined" value={overview.by_state?.Determination || 0} />
            <MetricCard label="Closed" value={overview.by_state?.Closed || 0} />
            <MetricCard label="Serious events" value={overview.serious_events} accent="red" />
          </div>
        )}

        {overview && (overview.reportable_fda_mdr > 0 || overview.reportable_eu_mir > 0 || overview.reportable_cdsco > 0) && (
          <div className="vq-card p-4 mb-6 border-l-4 border-l-red-500 flex items-center gap-3 flex-wrap">
            <AlertOctagon className="w-5 h-5 text-red-600" />
            <span className="font-mono text-xs uppercase tracking-wider text-red-700">Regulatory reportability queue</span>
            <span className="text-sm text-slate-700">
              FDA MDR: <b>{overview.reportable_fda_mdr}</b> • EU MIR: <b>{overview.reportable_eu_mir}</b> • CDSCO: <b>{overview.reportable_cdsco}</b>
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 vq-card overflow-hidden">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 vq-overline">
              All Complaints ({list.length})
            </div>
            <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {list.map((c) => (
                <button key={c.complaint_id} data-testid={`complaint-row-${c.complaint_id}`}
                        onClick={() => loadDetail(c.complaint_id)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${detail?.complaint_id === c.complaint_id ? "bg-slate-100" : ""}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${
                      c.patient_harm === "Death" ? "bg-black text-white" :
                      c.patient_harm === "Serious" ? "bg-red-100 text-red-700" :
                      c.patient_harm === "Minor" ? "bg-amber-100 text-amber-700" :
                      "bg-slate-100 text-slate-700"
                    }`}>{c.patient_harm}</span>
                    <span className="text-[10px] font-mono text-slate-500">{c.state}</span>
                    {c.determination?.fda_mdr_reportable && (
                      <span className="text-[9px] font-mono bg-red-50 text-red-700 border border-red-200 rounded px-1">FDA MDR</span>
                    )}
                    {c.determination?.eu_mir_reportable && (
                      <span className="text-[9px] font-mono bg-red-50 text-red-700 border border-red-200 rounded px-1">EU MIR</span>
                    )}
                  </div>
                  <div className="font-semibold text-sm text-slate-900 truncate">
                    {c.reporter_type} · {c.device_name || "—"}
                  </div>
                  <div className="text-[10px] font-mono text-slate-400 truncate">{c.complaint_id} · {c.narrative_scrubbed?.slice(0, 60)}…</div>
                </button>
              ))}
              {list.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No complaints yet.</div>}
            </div>
          </div>

          <div className="lg:col-span-3">
            {detail ? (
              <>
                <div className="vq-card p-6 mb-4">
                  <div className="vq-overline mb-1 font-mono">{detail.complaint_id}</div>
                  <h2 className="font-display font-bold text-xl text-slate-900 mb-1">
                    {detail.reporter_type} report · {detail.device_name || "device unspecified"}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono text-slate-500 mb-3">
                    {detail.country && <><Globe2 className="w-3 h-3" /> {detail.country}</>}
                    {detail.event_date && <>· Event: {detail.event_date}</>}
                    {detail.reporter_name && <>· Reporter: {detail.reporter_name}</>}
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap mb-4">
                    {detail.narrative_scrubbed || detail.narrative}
                  </p>

                  {/* State pipeline */}
                  <div className="flex items-center gap-2 flex-wrap mb-4">
                    {STATES.map((s, i) => {
                      const idx = STATES.indexOf(detail.state);
                      return (
                        <div key={s} className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-[10px] font-mono ${
                            s === detail.state ? "bg-slate-900 text-white" :
                            i < idx ? "bg-emerald-100 text-emerald-800" :
                            "bg-slate-100 text-slate-500"
                          }`}>{s}</span>
                          {i < STATES.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300" />}
                        </div>
                      );
                    })}
                  </div>

                  {/* Reportability hints & determination */}
                  <div className="grid md:grid-cols-2 gap-3 mb-4">
                    <div className="bg-slate-50 border border-slate-200 rounded p-3">
                      <div className="vq-overline mb-2">AI reportability hints</div>
                      <div className="text-xs text-slate-700 space-y-1 font-mono">
                        <div>FDA MDR: {detail.reportability_hints?.fda_mdr_reportable_suggested ? "Suggested (30-day clock)" : "Not suggested"}</div>
                        <div>EU MIR: {detail.reportability_hints?.eu_mir_reportable_suggested ? "Suggested (15-day clock)" : "Not suggested"}</div>
                        <div>CDSCO: {detail.reportability_hints?.cdsco_reportable_suggested ? "Suggested" : "Not suggested"}</div>
                      </div>
                    </div>
                    <div className={`border rounded p-3 ${detail.determination ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                      <div className="vq-overline mb-2">QA/RA determination</div>
                      {detail.determination ? (
                        <div className="text-xs text-slate-700 space-y-1">
                          <div><b>By:</b> {detail.determination.determined_by}</div>
                          <div className="font-mono">FDA: {detail.determination.fda_mdr_reportable ? "Yes" : "No"} · EU: {detail.determination.eu_mir_reportable ? "Yes" : "No"} · CDSCO: {detail.determination.cdsco_reportable ? "Yes" : "No"}</div>
                          <div className="italic">&ldquo;{detail.determination.rationale}&rdquo;</div>
                        </div>
                      ) : (
                        <div className="text-xs text-slate-600">Awaiting QA/RA sign-off.</div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {!detail.determination && (
                      <Button onClick={() => setOpenDet(true)} data-testid="complaint-determination-btn"
                              className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                        <ShieldCheck className="w-4 h-4 mr-2" /> Set determination
                      </Button>
                    )}
                    {STATES.indexOf(detail.state) < STATES.length - 1 && (
                      <Button variant="outline" onClick={() => requestTransition(STATES[STATES.indexOf(detail.state) + 1])}
                              data-testid="complaint-transition-btn"
                              className="border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-semibold">
                        <ArrowRight className="w-4 h-4 mr-2" /> Advance to {STATES[STATES.indexOf(detail.state) + 1]}
                      </Button>
                    )}
                    {!detail.linked_capa_id ? (
                      <Button variant="outline" onClick={openLinkCapa} data-testid="complaint-link-capa-btn"
                              className="border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white">
                        <Link2 className="w-4 h-4 mr-2" /> Link to CAPA
                      </Button>
                    ) : (
                      <div className="text-xs font-mono text-emerald-700">✓ Linked to {detail.linked_capa_id}</div>
                    )}
                  </div>
                </div>

                <AuditTrailPanel entityType="complaint" entityId={detail.complaint_id} refreshKey={refreshKey} />
              </>
            ) : (
              <div className="vq-card p-10 text-center text-slate-500">Select a complaint to view details.</div>
            )}
          </div>
        </div>
      </div>

      {/* New complaint dialog */}
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Log Complaint / PMS Event</DialogTitle>
            <DialogDescription>Personal identifiers will be automatically scrubbed before AI processing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Reporter type</Label>
                <Select value={form.reporter_type} onValueChange={(v) => setForm({ ...form, reporter_type: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{REPORTER_TYPES.map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                </Select></div>
              <div><Label className="text-xs font-semibold">Reporter name (optional)</Label>
                <Input value={form.reporter_name} data-testid="complaint-form-reporter"
                       onChange={(e) => setForm({ ...form, reporter_name: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Contact email (optional)</Label>
                <Input type="email" value={form.contact_email}
                       onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-xs font-semibold">Country</Label>
                <Input value={form.country}
                       onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5" placeholder="US / EU / IN…" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Device name</Label>
                <Input value={form.device_name} data-testid="complaint-form-device"
                       onChange={(e) => setForm({ ...form, device_name: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-xs font-semibold">Event date</Label>
                <Input type="date" value={form.event_date}
                       onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label className="text-xs font-semibold">Narrative</Label>
              <Textarea value={form.narrative} data-testid="complaint-form-narrative"
                        onChange={(e) => setForm({ ...form, narrative: e.target.value })}
                        rows={5} className="mt-1.5" placeholder="What happened, when, and to whom (identifiers will be redacted)." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Patient harm</Label>
                <Select value={form.patient_harm} onValueChange={(v) => setForm({ ...form, patient_harm: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="complaint-form-harm"><SelectValue /></SelectTrigger>
                  <SelectContent>{HARM.map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                </Select></div>
              <div className="flex items-end gap-2 pb-1.5">
                <Checkbox id="death-inj" checked={form.device_involved_in_death_or_serious_injury}
                          onCheckedChange={(v) => setForm({ ...form, device_involved_in_death_or_serious_injury: !!v })}
                          data-testid="complaint-form-serious" />
                <Label htmlFor="death-inj" className="text-xs">Death / serious injury involvement</Label>
              </div>
            </div>
            {seriousHint && (
              <div className="text-[11px] font-mono bg-red-50 border border-red-200 text-red-700 rounded p-2">
                ⚠ Serious event — FDA MDR / EU MIR clocks (30 / 15 days) will be suggested. QA/RA sign-off required for final reportability.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenForm(false)}>Cancel</Button>
            <Button onClick={create} data-testid="complaint-form-submit" className="bg-slate-900 hover:bg-[#0055FF] text-white">Log</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Determination dialog */}
      <Dialog open={openDet} onOpenChange={setOpenDet}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Regulatory determination</DialogTitle>
            <DialogDescription>QA/RA sign-off on reportability. Rationale is retained in the audit trail.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Root-cause summary (optional)</Label>
              <Textarea value={det.root_cause_summary}
                        onChange={(e) => setDet({ ...det, root_cause_summary: e.target.value })}
                        rows={2} className="mt-1.5" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center gap-2">
                <Checkbox id="fda" checked={det.fda_mdr_reportable}
                          onCheckedChange={(v) => setDet({ ...det, fda_mdr_reportable: !!v })}
                          data-testid="complaint-det-fda" />
                <Label htmlFor="fda" className="text-xs">FDA MDR</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="eu" checked={det.eu_mir_reportable}
                          onCheckedChange={(v) => setDet({ ...det, eu_mir_reportable: !!v })}
                          data-testid="complaint-det-eu" />
                <Label htmlFor="eu" className="text-xs">EU MIR</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="cdsco" checked={det.cdsco_reportable}
                          onCheckedChange={(v) => setDet({ ...det, cdsco_reportable: !!v })}
                          data-testid="complaint-det-cdsco" />
                <Label htmlFor="cdsco" className="text-xs">CDSCO</Label>
              </div>
            </div>
            <div><Label className="text-xs font-semibold">Rationale</Label>
              <Textarea value={det.rationale} data-testid="complaint-det-rationale"
                        onChange={(e) => setDet({ ...det, rationale: e.target.value })}
                        rows={3} className="mt-1.5" placeholder="Why this determination — cite 21 CFR 803, EU MDR Art. 87, MvPI as applicable." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDet(false)}>Cancel</Button>
            <Button onClick={submitDetermination} data-testid="complaint-det-submit"
                    className="bg-slate-900 hover:bg-[#0055FF] text-white">Save determination</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link CAPA dialog */}
      <Dialog open={openCapa} onOpenChange={setOpenCapa}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Link to CAPA</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {capaOptions.map((c) => (
              <button key={c.capa_id}
                      onClick={() => setSelectedCapa(c.capa_id)}
                      data-testid={`capa-option-${c.capa_id}`}
                      className={`w-full text-left border rounded p-2 hover:bg-slate-50 ${selectedCapa === c.capa_id ? "border-[#0055FF] bg-blue-50" : "border-slate-200"}`}>
                <div className="text-xs font-mono text-slate-500">{c.capa_id} · {c.state}</div>
                <div className="font-semibold text-sm text-slate-900 truncate">{c.title}</div>
              </button>
            ))}
            {capaOptions.length === 0 && <div className="text-sm text-slate-500 p-4 text-center">No CAPAs available.</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCapa(false)}>Cancel</Button>
            <Button onClick={doLinkCapa} disabled={!selectedCapa} data-testid="complaint-link-capa-submit"
                    className="bg-slate-900 hover:bg-[#0055FF] text-white">Link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {pending && (
        <ESignatureDialog
          open={esignOpen} onOpenChange={setEsignOpen}
          entityType="complaint" entityId={pending.id}
          action={`complaint_transition_${pending.toState}`}
          defaultMeaning="Review"
          title={`Sign to advance to ${pending.toState}`}
          onSigned={onSigned}
        />
      )}
    </DashboardLayout>
  );
}

function MetricCard({ label, value, accent }) {
  const color = accent === "red" ? "text-red-700" : "text-slate-900";
  return (
    <div className="vq-card p-3">
      <div className="vq-overline text-[9px]">{label}</div>
      <div className={`font-display font-bold text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );
}
